import assertNever from "assert-never";
import { isLoading, loading, Loading } from "../Loading";
import { HRApplicationFormSettings, HRQuestion, HRSection, PossibleAnswer, PossibleAnswerId, HRSectionStage, CaregiverDocumentTypeUploadWithDocumentTypeId } from "../messages/HR";
import * as React from "react";
import { API } from "../API";
import { HRQuestionGroupWithStatus, HRQuestionWithStatus, HRSectionWithStatus, HRSubSectionWithStatus } from "../models/HR";
import { HRDocumentTypeId, HRQuestionGroupId, HRQuestionId, HRSectionId, HRSubSectionId, CertificationSettings } from "../messages";
import { GenericSectionList } from "../components/GenericSectionsList";
import { Intent, Spinner } from "@blueprintjs/core";
import CaregiverInfo from "../components/widgets/CaregiverInfo";
import { evaluateQuestionStatus, evaluateSectionStatus, evaluateSubSectionStatusAndCompleted, evaluateQuestionGroupStatus } from "../QuestionUtils/StatusEvaluator";
import { AgencyId, CaregiverCertification, HRSectionStageId } from "../messages/ObjectIds";
import { Globals } from "../Globals";
import { formatPhone } from "../phone_number";
import { checkMobile } from "../Utils";
import { useSelector } from "react-redux";
import { useEffect, useReducer, useState } from "react";
import { AppState } from "src/store/store";

interface UIHierarchy {
    sectionWithStatus: HRSectionWithStatus;
    subSectionWithStatus: HRSubSectionWithStatus;
    groupsWithStatus: HRQuestionGroupWithStatus;
    questionWithStatus: HRQuestionWithStatus<HRQuestion>;
}

interface CaregiverFormEntryActivityProps {
    api: API;
    photoUrl: string | null;
    matchUrl: string;
    caregiverCertifications: CaregiverCertification[];
    onCertificationChange: (certification: CaregiverCertification) => void;
}

export function isSectionDocumentSection(section: HRSectionWithStatus) {
    return section.data.title === "Documents";
}

const CaregiverFormEntryActivity = (props: CaregiverFormEntryActivityProps) => {
    const [sections, setSections] = useState<HRSectionWithStatus[] | Loading>(loading());
    const [nestedQuestions, setNestedQuestions] = useState<HRQuestion[]>([]);
    const [currentStageId, setCurrentStageId] = useState<HRSectionStageId | null>(null);
    const [clickedSubSection, setClickedSubSection] = useState<HRSubSectionId | null>(null);
    const [clickedSection, setClickedSection] = useState<HRSectionId | null>(null);
    const [docTypesNames, setDocTypesNames] = useState<string[]>([]);
    const [docSection, setDocSection] = useState<HRSectionWithStatus | null>(null);
    const [settings, setSettings] = useState<HRApplicationFormSettings | null>(null);
    const [reviewPageText, setReviewPageText] = useState<string | null>(null);

    const selectedCertification: CaregiverCertification | null = useSelector((state: AppState) => state.certifications.selectedCertification);

    if (selectedCertification === null) {
        throw new Error("Can't fetch data without caregiver certification");
    }

    /* KEPT FOR CARE, AFTER MOVING FROM CLASS COMPONENTS */
    const [, forceUpdate] = useReducer(x => x + 1, 0);

    const findFirstAndLastIndex = (hrSectionWithStatus: HRSectionWithStatus[]) => {
        let firstStop = 0;
        let lastStop = undefined;
        let index = 0;
        let lastFlag = false;

        for (const section of hrSectionWithStatus) {
            if (section.data.isStop) {
                if (lastFlag && lastStop === undefined) {
                    lastStop = index;
                    setReviewPageText(section.data.title);
                    break;
                } else {
                    firstStop = index + 1;
                }
            } else if (section.status !== 100) {
                lastFlag = true;
            }

            index++;
        }

        return { firstStopIndex: firstStop, lastStopIndex: lastStop };
    };

    const spliceDocumentSectionFromSections = (hrSectionWithStatus: HRSectionWithStatus[]): HRSectionWithStatus | null => {
        let index = 0;
        for (const section of hrSectionWithStatus) {
            if (isSectionDocumentSection(section)) {
                return hrSectionWithStatus.splice(index, 1)[0];
            }
            index++;
        }

        return null;
    };

    const navigateToSection = (sections: HRSectionWithStatus[]): { clickedSection: HRSectionId, clickedSubSection: HRSubSectionId } => {
        for (const section of sections) {
            if (!section.data.isStop && !section.isLocked) {
                for (const subSection of section.subSections) {
                    if (subSection.status !== 100 || (subSection.status === 100 && subSection.document !== null && !subSection.document.isDocumentGenerated)) {
                        window.location.href = "#" + props.matchUrl + "/" + section.data.id + "/" + subSection.data.id;
                        return { clickedSection: section.data.id, clickedSubSection: subSection.data.id };
                    }
                }
            }
        }

        window.location.href = "#" + props.matchUrl + "/ReviewPage";
        return ({ clickedSection: HRSectionId.wrap(-999), clickedSubSection: HRSubSectionId.wrap(1) });
    };

    const areAllSectionsDone = (): boolean => {
        return true;
        // if (isLoading(sections)) {
        //     return false;
        // }
        // return sections.find((section) => section.status !== 100) === undefined;
    };

    const parseServerTextToDisplayText = (questionText: string, isMandatory: boolean | null) => {
        let tempText = questionText;
        if (isMandatory === true) {
            tempText = "*" + tempText;
        }
        const agencyNameRegex = new RegExp("{agency_name}", "g");
        const agencyAddressRegex = new RegExp("{agency_address}", "g");
        const agencyPhoneRegex = new RegExp("{agency_phone}", "g");
        const caregiverNameRegex = new RegExp("{caregiver_name}", "g");
        const caregiverCertificationRegex = new RegExp("{certification_type}", "g");
        const requiredFilesRegex = new RegExp("{required_files}", "g");

        let agencyAddress = props.api.authInfo.agencyMemberAuthInfo.agency.address;
        let agencyPhone = props.api.authInfo.agencyMemberAuthInfo.agency.officePhone;
        if (agencyAddress === null) { agencyAddress = "agencyAddress"; }
        if (agencyPhone === null) {
            agencyPhone = "agencyPhone";
        } else {
            agencyPhone = formatPhone(agencyPhone);
        }

        return tempText.replace(agencyNameRegex, props.api.authInfo.agencyMemberAuthInfo.agency.name)
            .replace(agencyAddressRegex, agencyAddress)
            .replace(agencyPhoneRegex, agencyPhone)
            .replace(caregiverNameRegex, props.api.authInfo.caregiver.firstName + " " + props.api.authInfo.caregiver.lastName)
            .replace(caregiverCertificationRegex, CaregiverCertification.unwrap(props.api.authInfo.caregiver.certifications[0]))
            .replace(requiredFilesRegex, docTypesNames.map(type => ` - ${type}`).join(" <br> "));
    };

    const addStatusToHrQuestion = (question: HRQuestion): HRQuestionWithStatus<HRQuestion>  => {
        const status = isLoading(evaluateQuestionStatus(question)) ? true : null;
        const parsedQuestionText = parseServerTextToDisplayText(question.questionText, question.isMandatory);
        switch (question.type) {
            case "Signature": {
                if (question.answer !== null) {
                    Globals.setLastUsedSignature(question.answer);
                }
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer
                    },
                    status: status
                };
            }
            case "GeneralSignature": {
                if (question.answer !== null) {
                    Globals.setLastUsedSignature(question.answer.signature);
                }
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer
                    },
                    status: status
                };
            }
            case "SSN": {
                if (question.answer !== null) {
                    Globals.setLastSSN(question.answer);
                }
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer
                    },
                    status: status
                };
            }
            case "BirthDate": {
                if (question.answer !== null) {
                    Globals.setLastBirthDate(question.answer);
                }
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer
                    },
                    status: status
                };
            }
            case "Dollar":
            case "StartAndEndTime":
            case "Numeric":
            case "Star":
            case "Address":
            case "PhoneNumber":
            case "Email":
            case "Text":
            case "YearDate":
            case "MonthYearDate":
            case "FullDate": {
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        questionText: parsedQuestionText,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer
                    },
                    status: status
                } as HRQuestionWithStatus<HRQuestion>;
            }
            case "Document":
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        questionText: parsedQuestionText,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer,
                        documentTypeToUpload: question.documentTypeToUpload,
                        canUploadFromGallery: question.canUploadFromGallery
                    },
                    status: status
                } as HRQuestionWithStatus<HRQuestion>;
            case "DocumentWithExpiry": {
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        questionText: parsedQuestionText,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer,
                        documentTypeToUpload: question.documentTypeToUpload
                    },
                    status: status
                } as HRQuestionWithStatus<HRQuestion>;
            }
            case "FreeText":
            case "Checkbox": {
                const temp: PossibleAnswer[] = question.possibleAnswers.map((answer): PossibleAnswer => {
                    return {
                        text: parseServerTextToDisplayText(answer.text, null),
                        id: answer.id
                    };
                });
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        questionText: parsedQuestionText,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        chosenAnswerIds: question.chosenAnswerIds,
                        possibleAnswers: temp
                    },
                    status: status
                } as HRQuestionWithStatus<HRQuestion>;
            }
            case "AmericanTest": {
                const toShuffle = question.possibleAnswers.map((answer) => {
                    return {
                        text: parseServerTextToDisplayText(answer.text, null),
                        id: answer.id,
                        order: Math.random()
                    };
                });
                const temp: PossibleAnswer[] = toShuffle.sort((answer1, answer2) => {
                    return answer1.order - answer2.order;
                }).map((answer): PossibleAnswer => {
                    return {
                        text: parseServerTextToDisplayText(answer.text, null),
                        id: answer.id
                    };
                });
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        questionText: parsedQuestionText,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        chosenAnswerId: question.chosenAnswerId,
                        possibleAnswers: temp,
                        correctAnswer: question.correctAnswer
                    },
                    status: status
                } as HRQuestionWithStatus<HRQuestion>;
            }
            case "Radio":
            case "DropDown": {
                const temp: PossibleAnswer[] = question.possibleAnswers.map((answer): PossibleAnswer => {
                    return {
                        text: parseServerTextToDisplayText(answer.text, null),
                        id: answer.id
                    };
                });
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        chosenAnswerId: question.chosenAnswerId,
                        possibleAnswers: temp
                    },
                    status: status
                } as HRQuestionWithStatus<HRQuestion>;
            }
            case "Placeholder": {
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                    },
                    status: status
                };
            }
            case "TitleText": {
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                    },
                    status: status
                };
            }
            case "PlainText": {
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        questionText: parsedQuestionText,
                        isMandatory: question.isMandatory,
                        type: question.type,
                    },
                    status: status
                };
            }
            case "DownloadableFile": {
                return {
                    question: {
                        id: question.id,
                        textId: question.textId,
                        downloaded: question.downloaded,
                        questionText: parsedQuestionText,
                        parentId: question.parentId,
                        parentAnswer: question.parentAnswer,
                        isHidden: question.isHidden,
                        isMandatory: question.isMandatory,
                        type: question.type,
                        answer: question.answer
                    },
                    status: status
                };
            }
            default: {
                return assertNever(question);
            }
        }
    };

    const addStatusToSections = (data: HRSection[], currentStageId: HRSectionStageId | null) => {
        const hrSectionWithStatus: HRSectionWithStatus[] = [];

        for (const section of data) {
            // add indication flag whether the section is locked or not
            const isLocked = section.data.HRSectionStageId !== null && (section.data.HRSectionStageId !== currentStageId);
            let sectionTemp: HRSectionWithStatus = {
                data: {
                    id: section.data.id,
                    title: parseServerTextToDisplayText(section.data.title, null),
                    subtitle: section.data.subtitle,
                    isStop: section.data.isStop,
                    HRSectionStageId: section.data.HRSectionStageId,
                    HRSectionStageLabel: section.data.HRSectionStageLabel
                },
                subSections: [],
                status: 0, // Calculated after pages are added
                isLocked: isLocked
            };
            let subSectionTemp: HRSubSectionWithStatus;
            for (const subSection of section.subSections) {
                subSectionTemp = {
                    data: {
                        id: subSection.data.id,
                        title: parseServerTextToDisplayText(subSection.data.title, null),
                        subtitle: subSection.data.subtitle,
                        settings: subSection.data.settings
                    },
                    document: subSection.document,
                    groups: [],
                    status: 0, // Calculated after groups are added
                    mandatoryCompleted: false // Calculated after groups are added
                };
                let questionGroupTemp: HRQuestionGroupWithStatus;
                for (const questionGroup of subSection.questionGroups) {
                    questionGroupTemp = {
                        data: {
                            id: questionGroup.data.id,
                            title: parseServerTextToDisplayText(questionGroup.data.title, null),
                            subtitle: questionGroup.data.subtitle
                        },
                        questions: [],
                        status: 0, // Calculated after questions are added,
                        mandatoryCompleted: false // Calculated after questions are added
                    };
                    let questionTemp: HRQuestionWithStatus<HRQuestion>;
                    for (const question of questionGroup.questions) {
                        questionTemp = addStatusToHrQuestion(question);
                        questionGroupTemp.questions.push(questionTemp);
                        const nestedQuestionsArray = nestedQuestions;
                        if (question.parentId !== null) {
                            nestedQuestionsArray.push(question);
                            setNestedQuestions(nestedQuestionsArray);
                        }
                    }
                    if (questionGroupTemp.questions.length > 0) {
                        evaluateQuestionGroupStatus(questionGroupTemp);
                        subSectionTemp.groups.push(questionGroupTemp);
                    }
                }
                if (subSectionTemp.groups.length > 0) {
                    evaluateSubSectionStatusAndCompleted(subSectionTemp);
                    sectionTemp.subSections.push(subSectionTemp);
                }
            }
            if (sectionTemp.subSections.length > 0 || sectionTemp.data.isStop) {
                evaluateSectionStatus(sectionTemp);
                hrSectionWithStatus.push(sectionTemp);
            }
        }

        return hrSectionWithStatus;
    };

    // TODO this doesn't take into account if files were uploaded and it should
    // CHECK IS GENERATED
    const skipStopSections = (hrSectionWithStatus: HRSectionWithStatus[]) => {
        const copy = hrSectionWithStatus.slice();
        const docSection = spliceDocumentSectionFromSections(copy);
        const { firstStopIndex, lastStopIndex } = findFirstAndLastIndex(copy);

        const toReturn = copy.slice(firstStopIndex, lastStopIndex);
        if (docSection !== null) {
            toReturn.push(docSection);
        }

        return toReturn;
    };


    const getCertificationPayRates = (): CertificationSettings => {
        if (settings === null) {
            return { locationPayRates: [] };
        }

        if (!settings.certificationSettings) {
            return { locationPayRates: [] };
        }
        return settings.certificationSettings;
    };

    const updateQuestionAnswer = (questionWithStatus: HRQuestionWithStatus<HRQuestion>, newAnswer: any) => {
        switch (questionWithStatus.question.type) {
            case "Dollar": {
                questionWithStatus.question.answer = "" + newAnswer;
                break;
            }
            case "SSN":
            case "Text":
            case "Email":
            case "PhoneNumber": {
                if (newAnswer === null || newAnswer.length === 0) {
                    questionWithStatus.question.answer = null;
                } else {
                    questionWithStatus.question.answer = newAnswer;
                }
                break;
            }
            case "Numeric": {
                questionWithStatus.question.answer = newAnswer;
                break;
            }
            case "FreeText":
            case "Checkbox": {
                // TODO comment this and put this in function
                if (questionWithStatus.question.chosenAnswerIds === null) {
                    questionWithStatus.question.chosenAnswerIds = [];
                }

                if (questionWithStatus.question.chosenAnswerIds.indexOf(newAnswer) === -1) {
                    questionWithStatus.question.chosenAnswerIds.push(newAnswer);
                } else {
                    if (questionWithStatus.question.chosenAnswerIds.length === 1) {
                        questionWithStatus.question.chosenAnswerIds = null;
                    } else {
                        questionWithStatus.question.chosenAnswerIds =
                            questionWithStatus.question.chosenAnswerIds.filter((answer: PossibleAnswerId) => answer !== newAnswer);
                    }
                }

                break;
            }
            case "AmericanTest":
            case "DropDown":
            case "Radio": {
                questionWithStatus.question.chosenAnswerId = newAnswer;
                // TODO when we have nested inputs they need to be taken into account
                break;
            }
            case "BirthDate":
            case "MonthYearDate":
            case "FullDate":
            case "YearDate":
            case "StartAndEndTime":
            case "Signature":
            case "GeneralSignature":
            case "Document":
            case "DocumentWithExpiry":
            case "Address":
            case "DownloadableFile": {
                questionWithStatus.question.answer = newAnswer;
                break;
            }
            case "Star": {
                if (newAnswer === 0) {
                    questionWithStatus.question.answer = null;
                } else {
                    questionWithStatus.question.answer = newAnswer;
                }
                break;
            }
            case "Placeholder":
            case "TitleText":
            case "PlainText": {
                break;
            }
            default: {
                assertNever(questionWithStatus.question);
            }
        }
    };

    /**
     * @returns Null if didn't find question
     */
    const getUIHierarchyById = (CaregiverForm: HRSectionWithStatus[], questionId: HRQuestionId, groupId: HRQuestionGroupId, subSectionId: HRSubSectionId, sectionId: HRSectionId): UIHierarchy | null => {
        const copy = CaregiverForm.slice();

        if (docSection !== null) {
            copy.push(docSection);
        }

        for (const section of copy) {
            if (section.data.id === sectionId) {
                for (const subSection of section.subSections) {
                    if (subSection.data.id === subSectionId) {
                        for (const group of subSection.groups) {
                            // if (group.data.id === groupId) {
                            for (const question of group.questions) {
                                if (question.question.id === questionId) {
                                    return {
                                        questionWithStatus: question,
                                        groupsWithStatus: group,
                                        subSectionWithStatus: subSection,
                                        sectionWithStatus: section
                                    };
                                }
                            }
                            // }
                        }
                    }
                }
            }
        }

        return null;
    };

    const evaluateHierarchyStatus = (hierarchy: UIHierarchy) => {
        evaluateQuestionGroupStatus(hierarchy.groupsWithStatus);
        evaluateSubSectionStatusAndCompleted(hierarchy.subSectionWithStatus);
        evaluateSectionStatus(hierarchy.sectionWithStatus);
        forceUpdate();
    };

    // TODO give type to newval
    const handleAnswerChange = (newVal: any, questionId: HRQuestionId, groupId: HRQuestionGroupId, subSectionId: HRSubSectionId, sectionId: HRSectionId) => {
        if (isLoading(sections)) {
            throw new Error("The impossible happened");
        }
        const newState = sections;

        const hierarchy = getUIHierarchyById(newState, questionId, groupId, subSectionId, sectionId);
        if (hierarchy === null) {
            throw new Error("The impossible happened");
        }

        updateQuestionAnswer(hierarchy.questionWithStatus, newVal);

        // check if current question has nested questions
        const nestedQuestionsArray = nestedQuestions.filter((question: HRQuestion) => question.parentId === hierarchy.questionWithStatus.question.id);
        nestedQuestionsArray.forEach(nestedQuestion => {
            const nestedhierarchy = getUIHierarchyById(newState, nestedQuestion.id, groupId, subSectionId, sectionId);
            if (nestedhierarchy && nestedhierarchy.questionWithStatus && nestedhierarchy.questionWithStatus.question) {
                const question = nestedhierarchy.questionWithStatus.question;
                if (nestedQuestion.parentAnswer === JSON.stringify(newVal)) {
                    question.isHidden = false;
                } else {
                    question.isHidden = true;
                }
            } else {
                console.log("did not find the nested question in state...");
            }
        });
        setSections(newState);
    };

    const getCaregiverDocumentTypeUploadObject = (question: HRQuestion): CaregiverDocumentTypeUploadWithDocumentTypeId => {
        switch (question.type) {
            case "Document": {
                if (question.answer === null) {
                    throw new Error("Cant upload empty document");
                }
                return {
                    caregiverDocumentTypeUpload: {
                        document: question.answer,
                        expiryDate: null
                    },
                    caregiverDocumentTypeId: question.documentTypeToUpload
                };
            }
            case "DocumentWithExpiry": {
                const answer = question.answer;
                const document = answer === null ? null :
                    answer.document === null ? null : answer.document;
                const expiryDate = answer === null ? null : answer.expiryDate;
                if (document === null) {
                    throw new Error("Cant upload empty document");
                }

                return {
                    caregiverDocumentTypeUpload: {
                        document: document,
                        expiryDate: expiryDate
                    },
                    caregiverDocumentTypeId: question.documentTypeToUpload
                };
            }
            default: {
                throw new Error("Cant upload docuemnt of such type");
            }
        }
    };

    const handleAnswerSave = async (questionId: HRQuestionId, groupId: HRQuestionGroupId, subSectionId: HRSubSectionId, sectionId: HRSectionId) => {
        if (isLoading(sections)) {
            throw new Error("The impossible happened");
        }

        const hierarchy = getUIHierarchyById(sections, questionId, groupId, subSectionId, sectionId);
        if (hierarchy === null) {
            throw new Error("The impossible happened");
        }

        hierarchy.questionWithStatus.status = evaluateQuestionStatus(hierarchy.questionWithStatus.question);

        const statusWhenSent = hierarchy.questionWithStatus.status;
        if (statusWhenSent === loading() || statusWhenSent === null) {
            hierarchy.questionWithStatus.status = loading();
            evaluateHierarchyStatus(hierarchy);

            let newStatus;
            try {
                const sectionId = hierarchy.sectionWithStatus.data.id;
                // Upload Caregiver Document, where sectionId is 1, otherwise upload regular answer
                if (sectionId === HRSectionId.wrap(-1)) {
                    const caregiverDocumentTypeUpload = getCaregiverDocumentTypeUploadObject(hierarchy.questionWithStatus.question);
                    newStatus = await props.api.uploadCaregiverDocument(caregiverDocumentTypeUpload);
                } else {
                    const toSendQuestion: HRQuestion = { ...hierarchy.questionWithStatus.question, questionText: "" };
                    newStatus = await props.api.putCaregiverForm(toSendQuestion);
                }
            } catch (e) {
                newStatus = { isSuccessful: false };
            }

            // This shows an error if the question was sent empty to the server
            if (statusWhenSent === null) {
                if (hierarchy.questionWithStatus.question.isMandatory) {
                    hierarchy.questionWithStatus.status = false;
                } else {
                    hierarchy.questionWithStatus.status = null;
                }
            } else {
                hierarchy.questionWithStatus.status = newStatus.isSuccessful;
            }
        }

        evaluateHierarchyStatus(hierarchy);
    };

    const handleIsDocumentGeneratedChange = (subSectionId: HRSubSectionId, sectionId: HRSectionId, form: HRSectionWithStatus[], isGenerated: boolean) => {
        for (const section of form) {
            if (section.data.id === sectionId) {
                for (const subSection of section.subSections) {
                    if (subSection.data.id === subSectionId) {
                        if (subSection.document !== null) {
                            subSection.document.isDocumentGenerated = isGenerated;
                            forceUpdate();
                        }
                        return;
                    }
                }
            }
        }
    };

    // Depending on the settings, might want to show all wrong questions
    const handleTestTryAgain = (sections: HRSectionWithStatus[], subSectionId: HRSubSectionId) => {
        for (const section of sections) {
            for (const sub of section.subSections) {
                if (sub.data.id === subSectionId && sub.data.settings !== null &&
                    sub.data.settings.testSettings !== null &&
                    sub.data.settings.testSettings.shouldShowWrongAnswers
                ) {
                    for (const group of sub.groups) {
                        for (const question of group.questions) {
                            if (question.question.type === "AmericanTest") {
                                if (question.question.chosenAnswerId !== question.question.correctAnswer) {
                                    question.status = false;
                                    evaluateHierarchyStatus({
                                        subSectionWithStatus: sub,
                                        sectionWithStatus: section,
                                        questionWithStatus: question,
                                        groupsWithStatus: group
                                    });
                                }
                            }
                        }
                    }

                    return;
                }
            }
        }
    };

    /**
     * This function is used to update section status, other than 'handleAnswerSave' function we have
     * because of different approach, e.g: 'PlaceholderDownloadableFile' component
     */
    const handleTriggerEvaluateHierarchyStatus = (questionId: HRQuestionId, groupId: HRQuestionGroupId, subSectionId: HRSubSectionId, sectionId: HRSectionId) => {
        if (isLoading(sections)) {
            throw new Error("Couldn't get state sections");
        }
        const hierarchy = getUIHierarchyById(sections, questionId, groupId, subSectionId, sectionId);
        if (hierarchy === null) {
            throw new Error("Couldn't get hierarchy");
        }

        hierarchy.questionWithStatus.status = true;
        if (hierarchy === null) {
            throw new Error("Couldn't get question status");
        }

        evaluateHierarchyStatus(hierarchy);
    };

    const asyncConstructor = async (props: CaregiverFormEntryActivityProps) =>  {
        const docTypes = await props.api.getDocumentTypesForCertification(selectedCertification);
        let docTypesNamesTemp: string[] = [];
        docTypes.documentTypes.forEach(doc => {
            let toPush = doc.name;
            if (props.api.authInfo.agencyMemberAuthInfo.agency.id === AgencyId.wrap(66)) {
                if (doc.id === HRDocumentTypeId.wrap(21) || doc.id === HRDocumentTypeId.wrap(27) ||
                    doc.id === HRDocumentTypeId.wrap(28) || doc.id === HRDocumentTypeId.wrap(29)) {
                    toPush += " (Provide Passport or Driver's License or Resident card or the Employment Authorization)";
                }
            }
            docTypesNamesTemp.push(toPush);
        });
        setDocTypesNames(docTypesNamesTemp);

        await props.api.setCaregiverStatus(selectedCertification);
        const form = await props.api.getCaregiverForm(selectedCertification);
        const sections = form.sections;
        const stages = await props.api.geHRStages(selectedCertification);

        // crates stages of id, by stages order
        let stageOrderArray: any = [];
        if (stages.stages.length > 0) {
            stageOrderArray = stages.stages.sort((a, b) => {
                return a.order - b.order;
            }).map((stage: HRSectionStage): any => {
                return stage.id;
            });
        }
        // if we have stage order array, then sort the sections by its orderArray
        if (stageOrderArray.length > 0) {
            sections.sort(function (a: HRSection, b: HRSection) {
                return stageOrderArray.indexOf(a.data.HRSectionStageId) - stageOrderArray.indexOf(b.data.HRSectionStageId);
            });
        }

        setSettings(form.settings);

        if (props.photoUrl !== null) {
            props.api.caregiverUploadPhoto(props.photoUrl);
        }

        // await delay(1000);
        // const sections = generateTempTestData();

        // We now add statuses
        const sectionsWithStatus = skipStopSections(addStatusToSections(sections, form.caregiverSectionStageId));
        const temp = navigateToSection(sectionsWithStatus);
        const forMobile = checkMobile(navigator.userAgent || navigator.vendor || (window as any).opera);
        setClickedSection(forMobile ? null : temp.clickedSection);
        setClickedSubSection(temp.clickedSubSection);
        setDocSection(spliceDocumentSectionFromSections(sectionsWithStatus));
        // setState({ sections: sectionsWithStatus, currentStageId: form.caregiverSectionStageId });
        setSections(sectionsWithStatus);
        setCurrentStageId(form.caregiverSectionStageId);
    };

    useEffect(() => {
        setClickedSubSection(null);
        setSections(loading());
        asyncConstructor(props);
    }, [selectedCertification]);

    if (!isLoading(sections) && clickedSubSection) {
        return (
            <div>
                <CaregiverInfo
                    caregiver={props.api.authInfo.caregiver}
                    photoUrl={props.photoUrl}
                    caregiverCertifications={props.caregiverCertifications}
                    onCertificationChange={props.onCertificationChange}
                />
                <GenericSectionList
                    sectionList={sections}
                    clickedSection={clickedSection}
                    clickedSubSection={clickedSubSection}
                    documentSection={docSection}
                    onAnswerChange={(newVal, questionId, groupId, subSectionId, sectionId) => handleAnswerChange(newVal, questionId, groupId, subSectionId, sectionId)}
                    onAnswerBlur={(questionId, groupId, subSectionId, sectionId) => handleAnswerSave(questionId, groupId, subSectionId, sectionId)}
                    onTriggerEvaluateHierarchyStatus={(questionId, groupId, subSectionId, sectionId) => handleTriggerEvaluateHierarchyStatus(questionId, groupId, subSectionId, sectionId)}
                    getCertificationPayRates={() => getCertificationPayRates()}
                    onDocumentGeneratedStatusChange={(subSectionId, isGenerated, sectionId) => handleIsDocumentGeneratedChange(subSectionId, sectionId, sections, isGenerated)}
                    onTestTryAgain={(subSectionId) => handleTestTryAgain(sections, subSectionId)}
                    api={props.api}
                    matchUrl={props.matchUrl}

                    docTypesNames={docTypesNames}
                    reviewPageText={reviewPageText === null ? "" : reviewPageText}
                    areAllSectionsDone={areAllSectionsDone}
                    selectedCertification={selectedCertification}
                />
            </div>
        );
    }

    return (
        <div className={"Globals__flexCenterColumn"}>
            <Spinner intent={Intent.PRIMARY} />
            <div>This may take a few seconds..</div>
        </div>
    );
};

export default CaregiverFormEntryActivity;



// WEBPACK FOOTER //
// ./src/activities/CaregiverFormEntryActivity.tsx